import { safeTiming, metricDefinitions, type TimingSummary } from './performanceSchema';
/** Fixed vocabulary only: never pass exception messages, URLs, stacks or payloads. */
export const operations = [
  'runtime',
  'save',
  'load',
  'import',
  'simulation',
  'publish',
  'editor',
  'graph',
  'render',
] as const;
export type DiagnosticOperation = (typeof operations)[number];
export const codes = ['failed', 'unhandled', 'worker', 'storage', 'timing'] as const;
export type DiagnosticCode = (typeof codes)[number];
export interface DiagnosticRecord {
  at: number;
  operation: DiagnosticOperation;
  code: DiagnosticCode;
  build: string;
  timing?: TimingSummary;
}
export const limits = {
  records: 200,
  pending: 64,
  bytes: 65536,
  ageMs: 7 * 86400000,
  suppressionMs: 5000,
};
export interface DiagnosticStore {
  append(records: DiagnosticRecord[], now: number): Promise<DiagnosticRecord[]>;
  read(now: number): Promise<DiagnosticRecord[]>;
  clear(): Promise<void>;
}
const operationSet = new Set<string>(operations);
const codeSet = new Set<string>(codes);
export function boundedRecords(input: unknown, now: number): DiagnosticRecord[] {
  if (!Array.isArray(input)) return [];
  const result: DiagnosticRecord[] = [];
  // Our database is capped on every write. Treat any foreign/malformed data as untrusted.
  for (const value of input.slice(-limits.records)) {
    if (!value || typeof value !== 'object') continue;
    const { at, operation, code, build } = value;
    if (
      !Number.isSafeInteger(at) ||
      at < now - limits.ageMs ||
      at > now ||
      !operationSet.has(operation) ||
      !codeSet.has(code) ||
      typeof build !== 'string' ||
      !/^(?:[a-f0-9]{7,40}|unknown)$/.test(build)
    )
      continue;
    if (code === 'timing') {
      const timing = safeTiming(value.timing);
      if (!timing || metricDefinitions[timing.metric].operation !== operation) continue;
      result.push({ at, operation, code, build, timing });
    } else result.push({ at, operation, code, build });
  }
  let bytes = 2;
  const bounded: DiagnosticRecord[] = [];
  for (let index = result.length - 1; index >= 0; index--) {
    const size = new TextEncoder().encode(JSON.stringify(result[index])).length + 1;
    if (bytes + size > limits.bytes) break;
    bytes += size;
    bounded.push(result[index]);
  }
  return bounded.reverse();
}

/** One app-scoped, injectable sink. No work on successful edits, ticks or frames. */
export class LocalDiagnostics {
  private pending: DiagnosticRecord[] = [];
  private recent = new Map<string, number>();
  private memory: DiagnosticRecord[] = [];
  private timer?: ReturnType<typeof setTimeout>;
  private serial: Promise<unknown> = Promise.resolve();
  private unavailable = false;
  private inFlight?: Promise<void>;
  private readonly build: string;
  constructor(
    private readonly store: DiagnosticStore,
    build: string | null = null,
    private readonly now: () => number = Date.now,
  ) {
    this.build = build && /^[a-f0-9]{7,40}$/.test(build) ? build : 'unknown';
  }
  record(operation: DiagnosticOperation, code: DiagnosticCode = 'failed'): void {
    // Runtime validation prevents accidental callers from turning this into a general logger.
    if (!operationSet.has(operation) || !codeSet.has(code) || code === 'timing') return;
    const at = this.now();
    const key = `${operation}:${code}`;
    if (at - (this.recent.get(key) ?? -Infinity) < limits.suppressionMs) return;
    this.recent.set(key, at); // Fixed vocabulary bounds the map, regardless of error volume.
    if (this.pending.length >= limits.pending) return;
    this.pending.push({ at, operation, code, build: this.build });
    if (!this.timer)
      this.timer = setTimeout(() => {
        this.timer = undefined;
        void this.flush();
      }, 1000);
  }
  recordTiming(input: TimingSummary): void {
    const timing = safeTiming(input);
    if (!timing || this.pending.length >= limits.pending) return;
    this.pending.push({
      at: this.now(),
      operation: metricDefinitions[timing.metric].operation,
      code: 'timing',
      build: this.build,
      timing,
    });
    if (!this.timer)
      this.timer = setTimeout(() => {
        this.timer = undefined;
        void this.flush();
      }, 1000);
  }
  private enqueue<T>(work: () => Promise<T>): Promise<T> {
    const result = this.serial.then(work);
    this.serial = result.catch(() => {});
    return result;
  }
  flush(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    if (this.inFlight) return this.inFlight;
    const batch = this.pending;
    this.pending = [];
    this.inFlight = this.enqueue(async () => {
      this.memory = boundedRecords([...this.memory, ...batch], this.now());
      if (this.unavailable) return;
      try {
        this.memory = await this.store.append(batch, this.now());
      } catch {
        this.unavailable = true;
      } // Never log recorder failures or retry in a loop.
    }).finally(() => {
      this.inFlight = undefined;
      if (this.pending.length && !this.timer)
        this.timer = setTimeout(() => {
          this.timer = undefined;
          void this.flush();
        }, 1000);
    });
    return this.inFlight;
  }
  async export() {
    await this.flush();
    if (this.pending.length) await this.flush();
    return this.enqueue(async () => {
      if (!this.unavailable) {
        try {
          this.memory = await this.store.read(this.now());
        } catch {
          this.unavailable = true;
        }
      }
      return {
        schemaVersion: 1,
        exportedAt: this.now(),
        persistence: this.unavailable ? 'memory-only' : 'indexeddb',
        limits: { ...limits },
        records: boundedRecords(this.memory, this.now()),
      };
    });
  }
  clear(): Promise<boolean> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.pending = [];
    this.recent.clear();
    return this.enqueue(async () => {
      this.memory = [];
      try {
        await this.store.clear();
        this.unavailable = false;
        return true;
      } catch {
        this.unavailable = true;
        return false;
      }
    });
  }
}
